import * as React from 'react';

import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import type {
  AirCardResponse,
  AiTeamCardState,
  AiTeamMetric,
} from 'src/types/ai-team';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter';
import logger from 'src/utils/logger';
import {startNewConversation} from 'src/components/agentic-ui/ask-ai-store';
import AiTeamCard, {
  type AiTeamCardProps,
} from 'src/components/dashboard/ai-team-card';
import {useAiRecruiterCard} from 'src/components/dashboard/api-hooks';


const SOURCE = 'ai_recruiter_card';

// All in-app CTAs land on the AI Recruiter v2 homepage; that page renders the
// right view (setup / sequences / canvas) based on the customer's progress.
const HOMEPAGE_ROUTE = '/ai-recruiter-v2';
// Fallback when the payload carries no reach_out_link.
const REQUEST_DEMO_URL =
  'https://1f399.share.hsforms.com/2UTchEbuuRwqDOXG8J_dkXQ';

// Defensive: malformed payloads resolve to 'unknown' → neutral placeholder (AC7),
// never a wrong-state CTA.
const deriveState = (response: AirCardResponse): AiTeamCardState => {
  if (
    typeof response?.is_enabled !== 'boolean' ||
    typeof response.is_setup_complete !== 'boolean'
  ) {
    return 'unknown';
  }
  if (!response.is_enabled) {
    return 'not_enabled';
  }
  if (!response.is_setup_complete) {
    return 'setup_pending';
  }
  // No analytics yet = nothing running.
  return response.analytics == null ? 'no_active_sequence' : 'active';
};

// Thin wrapper: fetches air_card, translates strings, and passes state-specific
// props to the generic card.
function AiRecruiterCard(): React.ReactElement {
  const {t} = useLabelTranslation();
  const history = useHistory();
  const {data, isPending, isError, refetch} = useAiRecruiterCard();

  if (isPending) {
    return (
      <Shimmer
        type="rounded"
        width="100%"
        height={192}
        testId="ai-recruiter-card-loading"
      />
    );
  }

  if (isError || data == null) {
    return (
      <InContextAlert
        semantic="danger"
        actionText={t('Retry', 'Retry')}
        actionTextColor="clickable"
        onAction={() => {
          void refetch();
        }}
      >
        {t('ai_team_card_load_error', {
          defaultValue: 'Couldn’t load AI Recruiter. Please try again.',
        })}
      </InContextAlert>
    );
  }

  const goToRecruiter = () => {
    void history.push(HOMEPAGE_ROUTE);
  };

  const state = deriveState(data);
  const sequenceTemplateId = data.sequence_template_id;

  const metrics: AiTeamMetric[] =
    data.analytics == null
      ? []
      : [
          {
            label: t('Jobs_Worked_Upon', 'Jobs Worked Upon'),
            value: data.analytics.jobs_worked_upon,
          },
          {
            label: t('Candidates_Screened', 'Candidates Screened'),
            value: data.analytics.candidates_screened,
          },
          {
            label: t('Avg_Time_to_Screen', 'Avg. Time to Screen'),
            value: data.analytics.avg_time_to_screen,
          },
        ];

  // Per-state body + CTAs (AEP-6575 HD-01 matrix). 'unknown' falls through to
  // the definition-only placeholder.
  function getStateProps(): Partial<AiTeamCardProps> {
    switch (state) {
      case 'not_enabled': {
        const learnMorePrompt = data?.learn_more_text?.trim();
        return {
          primaryAction: {
            label: t('Request_Demo', 'Request Demo'),
            iconRightName: 'play',
            onClick: () =>
              window.open(data?.reach_out_link || REQUEST_DEMO_URL, '_blank'),
          },
          // Learn More opens a new Ask AI conversation seeded with the
          // backend-provided prompt; omitted when the payload has no prompt.
          secondaryAction: learnMorePrompt
            ? {
                label: t('Learn_More', 'Learn More'),
                iconRightName: 'arrow-right',
                onClick: () => {
                  const started = startNewConversation({
                    source: SOURCE,
                    message: learnMorePrompt,
                  });
                  if (!started) {
                    logger.info(
                      'ai recruiter card: chat panel not mounted, cannot start conversation',
                    );
                  }
                },
              }
            : undefined,
        };
      }
      case 'setup_pending':
        return {
          lead: t('ai_recruiter_setup_lead', {
            defaultValue: 'Start using AI Recruiter.',
          }),
          description: t('ai_recruiter_setup_description', {
            defaultValue: 'Finish set-up It only takes a few minutes.',
          }),
          primaryAction: {
            label: t('Finish_Set_Up', 'Finish Set Up'),
            onClick: goToRecruiter,
          },
        };
      case 'no_active_sequence':
        return {
          lead: t('ai_recruiter_activate_lead', {
            defaultValue: 'Activate your first sequences.',
          }),
          description: t('ai_recruiter_activate_description', {
            defaultValue: 'Put AI Recruiter to work.',
          }),
          primaryAction: {
            label: t('Activate_a_Sequence', 'Activate a Sequence'),
            onClick: goToRecruiter,
          },
        };
      case 'active':
        return {
          metrics,
          primaryAction: {
            label: t('View_Details', 'View Details'),
            // Rollup performance page for all sequences of the backing template.
            onClick: () => {
              void history.push(
                sequenceTemplateId != null
                  ? `/sequence-template/${sequenceTemplateId}/performance`
                  : HOMEPAGE_ROUTE,
              );
            },
          },
        };
      default:
        return {};
    }
  }

  const stateProps = getStateProps();

  return (
    <AiTeamCard
      state={state}
      title={t('AI_Recruiter', 'AI Recruiter')}
      description={
        stateProps.description ??
        t('ai_recruiter_card_description', {
          defaultValue:
            'Source, screen, and follow-up with candidates automatically.',
        })
      }
      testId="ai-recruiter-card"
      {...stateProps}
    />
  );
}

export default AiRecruiterCard;
